import { useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import DeviceLaptop from "../ui/DeviceLaptop";
import { useTheme } from "../theme/ThemeProvider";
import Button from "../ui/Button";
import { ArrowRight } from "../ui/icons";
import { CONSOLE_SHOTS, CONSOLE_SIZE, consoleSrc } from "../data/console";
import { COMPANY } from "../data/company";
import "./desk.css";

const CONSOLE_URL = `${COMPANY.urls.console}/login`;

/**
 * The console, at desk size.
 *
 * Everything else on this page is a phone; this is the one act that answers
 * "what does it look like on the computer at the front desk", which is the
 * question a gym owner with a receptionist asks first. A laptop rather than a
 * desktop display, because that is what sits on most front desks, and its lid
 * opens as the section arrives.
 *
 * The screens are switched by the reader rather than by the scroll. The
 * showcase above already spends a full viewport-height of scroll driving a
 * sequence, and a page that takes the scrollbar away twice has stopped being
 * a page and started being a slideshow.
 */
export default function Desk() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center center"],
  });

  /*
   * Spring the progress rather than reading it raw. A tilt wired straight to
   * the scroll position stops dead the instant the wheel does; a spring keeps
   * the panel settling for a few frames after, which is what a physical object
   * on a hinge does.
   */
  const settled = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 24,
    mass: 0.7,
  });

  /*
   * The lid opens on its hinge, from nearly shut to upright, while the whole
   * machine rises a little and comes to full size. The same reading as the
   * monitor's tilt it replaces: the display meets the reader as they arrive.
   */
  const lidX = useTransform(settled, [0, 1], [reduce ? 0 : -68, 0]);
  const scale = useTransform(settled, [0, 1], [reduce ? 1 : 0.9, 1]);
  const y = useTransform(settled, [0, 1], [reduce ? 0 : 48, 0]);

  const shot = CONSOLE_SHOTS[active];

  return (
    <section className="section desk" id="desk" ref={ref}>
      <div className="container desk__inner">
        <h2 className="t-display-2 desk__title">
          The same gym,
          <br />
          on the screen at the desk.
        </h2>
        <p className="t-lead desk__lead">
          There is nothing to install. Open a browser at the front desk and the
          web app is the one you already know from your phone, laid out for a
          keyboard.
        </p>

        <div className="desk__stage">
          <motion.div className="desk__machine" style={{ scale, y }}>
            <DeviceLaptop Lid={motion.div} lidStyle={{ rotateX: lidX }}>
              <div className="desk__screen">
                {/*
                 * Layered and cross-faded rather than swapped, so there is
                 * never a frame with no screen in the bezel. The blur is what
                 * makes the swap read as one display changing rather than as
                 * two images stacked: without it you see both layouts at once
                 * through the middle of the transition.
                 */}
                <AnimatePresence initial={false}>
                  <motion.div
                    className="desk__layer"
                    key={shot.slug}
                    initial={
                      reduce
                        ? { opacity: 0 }
                        : { opacity: 0, filter: "blur(10px)", scale: 1.015 }
                    }
                    animate={
                      reduce
                        ? { opacity: 1 }
                        : { opacity: 1, filter: "blur(0px)", scale: 1 }
                    }
                    exit={
                      reduce
                        ? { opacity: 0 }
                        : { opacity: 0, filter: "blur(10px)", scale: 0.99 }
                    }
                    transition={{
                      duration: reduce ? 0.2 : 0.42,
                      ease: [0.25, 1, 0.5, 1],
                    }}
                  >
                    <ConsoleImage slug={shot.slug} screen={shot.screen} />
                  </motion.div>
                </AnimatePresence>
              </div>
            </DeviceLaptop>
          </motion.div>
        </div>

        <div className="desk__chapters" role="tablist" aria-label="Console screens">
          {CONSOLE_SHOTS.map((s, i) => (
            <button
              className="desk__chapter"
              key={s.slug}
              type="button"
              role="tab"
              aria-current={i === active}
              aria-selected={i === active}
              onClick={() => setActive(i)}
            >
              {s.screen}
            </button>
          ))}
        </div>

        <div className="desk__caption">
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              className="desk__caption-item"
              key={shot.slug}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10 }}
              animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, y: -10 }}
              transition={{ duration: 0.22, ease: [0.25, 1, 0.5, 1] }}
            >
              <h3 className="desk__caption-headline">
                {shot.headline.split("\n").map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </h3>
              <p className="t-lead">{shot.subline}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        <Button href={CONSOLE_URL} variant="primary" size="lg">
          Start free in your browser
          <span className="btn__glyph">
            <ArrowRight />
          </span>
        </Button>
      </div>
    </section>
  );
}

/* The capture in the page's own appearance, like every phone on the site. */
function ConsoleImage({ slug, screen }) {
  const { theme } = useTheme();
  return (
    <picture>
      <source srcSet={consoleSrc(slug, theme, "webp")} type="image/webp" />
      <img
        key={theme}
        // Before src: see ShotImage in ui/Screen.jsx.
        loading="lazy"
        decoding="async"
        src={consoleSrc(slug, theme, "png")}
        alt={`The ${screen} screen of the Varzish Mate web app in a desktop browser.`}
        width={CONSOLE_SIZE.width}
        height={CONSOLE_SIZE.height}
        draggable="false"
      />
    </picture>
  );
}
